import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import KpiCard from '../../components/KpiCard';
import { styles, colors, radius } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const StatusBadge = ({ status }) => {
  const style = styles.statusColors[status] || { bg: '#e9ecef', color: '#495057' };
  const labels = {
    'YENI': 'Yeni',
    'GORULDU': 'Goruldu',
    'TEMIN_EDILDI': 'Temin Edildi',
    'IPTAL': 'İptal',
    'BEKLENIYOR': 'Bekliyor',
    'ONAYLANDI': 'Onaylandı'
  };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: radius.xl,
      fontSize: '11px', fontWeight: 'bold', background: style.bg, color: style.color
    }}>
      {labels[status] || status}
    </span>
  );
};

const IhtiyacListesiPage = () => {
  const { user } = useAuth();
  const [talepler, setTalepler] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  // Filtre state
  const [filterDurum, setFilterDurum] = useState('AKTIF');
  const [filterLokasyon, setFilterLokasyon] = useState('');

  // Form state — çoklu ürün girişi
  const [showForm, setShowForm] = useState(false);
  const [formLokasyon, setFormLokasyon] = useState('');
  const [formAçıklama, setFormAçıklama] = useState('');
  const [formSatirlar, setFormSatirlar] = useState([
    { malzeme_id: '', serbest_urun_ad: '', miktar: '', birim: 'kg', serbest: false }
  ]);

  // Checkbox state (toplu goruldu icin)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Gün bazlı acik/kapali state
  const [openDays, setOpenDays] = useState({});

  const showMsg = useCallback((msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  }, []);

  const fetchTalepler = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/talepler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTalepler(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('Talepler fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTalepler();
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(d => setLokasyonlar(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(d => setMalzemeler(Array.isArray(d) ? d : [])).catch(() => {});
  }, [fetchTalepler]);

  // Auto-refresh 30sn
  useEffect(() => {
    const interval = setInterval(fetchTalepler, 30000);
    return () => clearInterval(interval);
  }, [fetchTalepler]);

  // Filtrelenmiş talepler
  const filteredTalepler = useMemo(() => {
    let list = talepler;
    if (filterDurum === 'AKTIF') {
      list = list.filter(t => ['YENI', 'GORULDU', 'BEKLENIYOR'].includes(t.durum));
    } else if (filterDurum) {
      list = list.filter(t => t.durum === filterDurum);
    }
    if (filterLokasyon) {
      list = list.filter(t => String(t.lokasyon_id) === filterLokasyon);
    }
    // Sube rolu icin sadece kendi lokasyonu (basit kontrol)
    return list;
  }, [talepler, filterDurum, filterLokasyon]);

  // Gun bazli gruplama
  const groupedByDay = useMemo(() => {
    const groups = {};
    filteredTalepler.forEach(t => {
      const d = new Date(t.created_at);
      const key = d.toISOString().split('T')[0]; // YYYY-MM-DD
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    // Sırala: en yeni gün üstte
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateKey, items]) => ({ dateKey, items }));
  }, [filteredTalepler]);

  // Bugun ve dun default acik
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    setOpenDays(prev => {
      const next = { ...prev };
      if (next[today] === undefined) next[today] = true;
      if (next[yesterday] === undefined) next[yesterday] = true;
      return next;
    });
  }, []);

  const toggleDay = (dateKey) => {
    setOpenDays(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  // KPI'lar
  const kpis = useMemo(() => {
    const yeni = talepler.filter(t => t.durum === 'YENI' || t.durum === 'BEKLENIYOR').length;
    const goruldu = talepler.filter(t => t.durum === 'GORULDU').length;
    const temin = talepler.filter(t => t.durum === 'TEMIN_EDILDI').length;
    const bugunList = talepler.filter(t => {
      const d = new Date(t.created_at);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    });
    return { yeni, goruldu, temin, bugun: bugunList.length };
  }, [talepler]);

  // Satir ekleme/silme/guncelleme
  const addSatir = () => {
    setFormSatirlar(prev => [...prev, { malzeme_id: '', serbest_urun_ad: '', miktar: '', birim: 'kg', serbest: false }]);
  };
  const removeSatir = (idx) => {
    if (formSatirlar.length <= 1) return;
    setFormSatirlar(prev => prev.filter((_, i) => i !== idx));
  };
  const updateSatir = (idx, field, value) => {
    setFormSatirlar(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  // Form submit — batch endpoint
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formLokasyon) { showMsg('Lokasyon seçiniz', 'error'); return; }

    const validSatirlar = formSatirlar.filter(s => {
      if (s.serbest) return s.serbest_urun_ad && s.serbest_urun_ad.trim() && parseFloat(s.miktar) > 0;
      return s.malzeme_id && parseFloat(s.miktar) > 0;
    });
    if (validSatirlar.length === 0) { showMsg('En az bir urun ekleyin', 'error'); return; }

    try {
      const body = {
        lokasyon_id: parseInt(formLokasyon),
        açıklama: formAçıklama || null,
        satirlar: validSatirlar.map(s => ({
          malzeme_id: s.serbest ? null : parseInt(s.malzeme_id),
          serbest_urun_ad: s.serbest ? s.serbest_urun_ad.trim() : null,
          miktar: parseFloat(s.miktar),
          birim: s.birim
        }))
      };
      const res = await fetch(`${API_URL}/api/talepler/batch`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      showMsg(data.message || 'Ihtiyaclar başarıyla eklendi');
      setFormSatirlar([{ malzeme_id: '', serbest_urun_ad: '', miktar: '', birim: 'kg', serbest: false }]);
      setFormLokasyon('');
      setFormAçıklama('');
      setShowForm(false);
      fetchTalepler();
    } catch (err) {
      showMsg('Ihtiyac eklenemedi: ' + err.message, 'error');
    }
  };

  // Goruldu
  const handleGoruldu = async (talepId) => {
    try {
      const res = await fetch(`${API_URL}/api/talepler/${talepId}/goruldu`, {
        method: 'PATCH', headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showMsg('Talep goruldu olarak isaretlendi');
      fetchTalepler();
    } catch (err) {
      showMsg('İşlem başarısız: ' + err.message, 'error');
    }
  };

  // Toplu goruldu
  const handleBatchGoruldu = async () => {
    if (selectedIds.size === 0) return;
    try {
      const res = await fetch(`${API_URL}/api/talepler/batch-goruldu`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ talep_ids: Array.from(selectedIds) })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      showMsg(`${data.updated} talep goruldu olarak isaretlendi`);
      setSelectedIds(new Set());
      fetchTalepler();
    } catch (err) {
      showMsg('Toplu islem başarısız: ' + err.message, 'error');
    }
  };

  // İptal
  const handleIptal = async (talepId) => {
    if (!window.confirm('Bu talebi iptal etmek istediğinize emin misiniz?')) return;
    try {
      const res = await fetch(`${API_URL}/api/talepler/${talepId}/iptal`, {
        method: 'PATCH', headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showMsg('Talep iptal edildi');
      fetchTalepler();
    } catch (err) {
      showMsg('İptal başarısız: ' + err.message, 'error');
    }
  };

  // Checkbox toggle
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (items) => {
    const yeniItems = items.filter(t => t.durum === 'YENI' || t.durum === 'BEKLENIYOR');
    const allSelected = yeniItems.every(t => selectedIds.has(t.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      yeniItems.forEach(t => {
        if (allSelected) next.delete(t.id); else next.add(t.id);
      });
      return next;
    });
  };

  const isSatinalmaOrYonetim = user?.role === 'satinalma' || user?.role === 'yonetim' || user?.role === 'admin';

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const days = ['Pazar', 'Pazartesi', 'Sali', 'Carsamba', 'Persembe', 'Cuma', 'Cumartesi'];
    const months = ['Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran', 'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'];
    const today = new Date();
    const yesterday = new Date(Date.now() - 86400000);
    let label = '';
    if (d.toDateString() === today.toDateString()) label = ' (Bugun)';
    else if (d.toDateString() === yesterday.toDateString()) label = ' (Dun)';
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} ${days[d.getDay()]}${label}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  // Yeni talepler var mi (batch goruldu butonu icin)
  const hasNewTalepler = selectedIds.size > 0;

  return (
    <div>
      <Header title="Ihtiyac Listesi" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Mesaj */}
        {message && (
          <div style={{
            background: messageType === 'error' ? '#f8d7da' : '#d4edda',
            padding: '12px 20px', marginBottom: '16px', borderRadius: radius.sm,
            color: messageType === 'error' ? '#721c24' : '#155724',
            borderLeft: `4px solid ${messageType === 'error' ? styles.danger : styles.success}`
          }}>
            {message}
          </div>
        )}

        {/* KPI Kartlari */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          <KpiCard title="Yeni Talepler" value={loading ? '...' : kpis.yeni} color="#1565c0" icon="📋" />
          <KpiCard title="Goruldu (Isleniyor)" value={loading ? '...' : kpis.goruldu} color="#e65100" icon="👁" />
          <KpiCard title="Temin Edildi" value={loading ? '...' : kpis.temin} color="#2e7d32" icon="✅" />
          <KpiCard title="Bugunun Talepleri" value={loading ? '...' : kpis.bugun} color={styles.info} icon="📅" />
        </div>

        {/* Aksiyon Bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '16px', flexWrap: 'wrap', gap: '10px'
        }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Durum filtreleri */}
            {[
              { key: 'AKTIF', label: 'Aktif' },
              { key: '', label: 'Tumu' },
              { key: 'YENI', label: 'Yeni' },
              { key: 'GORULDU', label: 'Goruldu' },
              { key: 'TEMIN_EDILDI', label: 'Temin Edildi' },
              { key: 'IPTAL', label: 'İptal' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilterDurum(f.key)}
                style={{
                  padding: '6px 14px', borderRadius: radius.xl, border: 'none', cursor: 'pointer',
                  fontSize: '12px', fontWeight: filterDurum === f.key ? 'bold' : 'normal',
                  background: filterDurum === f.key ? styles.primary : '#e9ecef',
                  color: filterDurum === f.key ? 'white' : '#495057'
                }}
              >
                {f.label}
              </button>
            ))}

            {/* Lokasyon filtresi */}
            <select
              value={filterLokasyon}
              onChange={(e) => setFilterLokasyon(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '12px' }}
            >
              <option value="">Tüm Lokasyonlar</option>
              {lokasyonlar.map(l => (
                <option key={l.id} value={l.id}>{l.ad}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Toplu goruldu butonu */}
            {isSatinalmaOrYonetim && hasNewTalepler && (
              <button
                onClick={handleBatchGoruldu}
                style={{
                  padding: '8px 16px', background: '#e65100', color: 'white',
                  border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '13px'
                }}
              >
                Secilenleri Gordum ({selectedIds.size})
              </button>
            )}
            {/* Yeni ihtiyac butonu */}
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                padding: '8px 16px', background: showForm ? '#6c757d' : styles.primary,
                color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer',
                fontWeight: 'bold', fontSize: '13px'
              }}
            >
              {showForm ? 'Kapat' : '+ Yeni Ihtiyac'}
            </button>
          </div>
        </div>

        {/* Yeni Ihtiyac Formu — Coklu Urun Girisi */}
        {showForm && (
          <div style={{
            background: colors.surface, borderRadius: radius.md, padding: '20px',
            marginBottom: '16px', border: `2px solid ${styles.primary}20`
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: styles.secondary }}>Yeni Ihtiyac Ekle</h3>
            <form onSubmit={handleSubmit}>
              {/* Ust satir: Lokasyon + Açıklama */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>Lokasyon *</label>
                  <select
                    value={formLokasyon}
                    onChange={(e) => setFormLokasyon(e.target.value)}
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px' }}
                  >
                    <option value="">Seçiniz...</option>
                    {lokasyonlar.map(l => (
                      <option key={l.id} value={l.id}>{l.ad} ({l.tip})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>Açıklama (Opsiyonel)</label>
                  <input
                    type="text"
                    value={formAçıklama}
                    onChange={(e) => setFormAçıklama(e.target.value)}
                    placeholder="Genel açıklama..."
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Urun satirlari */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 100px 80px 30px', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', color: '#888', fontWeight: 'bold' }}>Malzeme</span>
                  <span style={{ fontSize: '11px', color: '#888', fontWeight: 'bold' }}>Miktar</span>
                  <span style={{ fontSize: '11px', color: '#888', fontWeight: 'bold' }}>Birim</span>
                  <span></span>
                </div>
                {formSatirlar.map((satir, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 100px 80px 30px', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                    <div>
                      {satir.serbest ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <input
                            type="text"
                            value={satir.serbest_urun_ad}
                            onChange={(e) => updateSatir(idx, 'serbest_urun_ad', e.target.value)}
                            placeholder="Urun adi yazin..."
                            style={{ flex: 1, padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box' }}
                          />
                          <button type="button" onClick={() => updateSatir(idx, 'serbest', false)}
                            style={{ padding: '4px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.backgroundAlt, cursor: 'pointer', fontSize: '10px', whiteSpace: 'nowrap' }}
                          >Liste</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <select
                            value={satir.malzeme_id}
                            onChange={(e) => updateSatir(idx, 'malzeme_id', e.target.value)}
                            style={{ flex: 1, padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
                          >
                            <option value="">Seçiniz...</option>
                            {malzemeler.map(m => (
                              <option key={m.id} value={m.id}>{m.ad} ({m.kategori})</option>
                            ))}
                          </select>
                          <button type="button" onClick={() => updateSatir(idx, 'serbest', true)}
                            style={{ padding: '4px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.backgroundAlt, cursor: 'pointer', fontSize: '10px', whiteSpace: 'nowrap' }}
                          >Yaz</button>
                        </div>
                      )}
                    </div>
                    <input
                      type="number" step="0.1" value={satir.miktar}
                      onChange={(e) => updateSatir(idx, 'miktar', e.target.value)}
                      placeholder="50"
                      style={{ padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box' }}
                    />
                    <select
                      value={satir.birim}
                      onChange={(e) => updateSatir(idx, 'birim', e.target.value)}
                      style={{ padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
                    >
                      <option value="kg">kg</option>
                      <option value="adet">adet</option>
                      <option value="litre">litre</option>
                      <option value="paket">paket</option>
                      <option value="koli">koli</option>
                    </select>
                    {formSatirlar.length > 1 ? (
                      <button type="button" onClick={() => removeSatir(idx)}
                        style={{ padding: '4px', background: '#f8d7da', color: '#721c24', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}
                      >×</button>
                    ) : <div />}
                  </div>
                ))}
                <button type="button" onClick={addSatir}
                  style={{
                    padding: '6px 14px', background: '#e9ecef', color: '#495057',
                    border: '1px dashed #adb5bd', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px', marginTop: '4px'
                  }}
                >
                  + Satir Ekle
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#888' }}>{formSatirlar.length} urun</span>
                <button type="submit"
                  style={{
                    padding: '10px 24px', background: styles.success, color: 'white',
                    border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px'
                  }}
                >
                  Kaydet ({formSatirlar.filter(s => s.serbest ? s.serbest_urun_ad : s.malzeme_id).length} urun)
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Gun Bazli Gruplu Liste */}
        {loading ? (
          <div style={{ background: colors.surface, borderRadius: radius.md, padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#888' }}>Yükleniyor...</div>
          </div>
        ) : groupedByDay.length === 0 ? (
          <div style={{ background: colors.surface, borderRadius: radius.md, padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#888' }}>Talep bulunamadı</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {groupedByDay.map(({ dateKey, items }) => {
              const isOpen = openDays[dateKey] !== false;
              const yeniCount = items.filter(t => t.durum === 'YENI' || t.durum === 'BEKLENIYOR').length;
              return (
                <div key={dateKey} style={{ background: colors.surface, borderRadius: radius.md, overflow: 'hidden' }}>
                  {/* Gun baslik */}
                  <div
                    onClick={() => toggleDay(dateKey)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 16px', cursor: 'pointer', background: colors.backgroundAlt,
                      borderBottom: isOpen ? `1px solid ${colors.border}` : 'none',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>{isOpen ? '▼' : '▶'}</span>
                      <span style={{ fontWeight: 'bold', fontSize: '14px', color: styles.secondary }}>
                        {formatDate(dateKey)}
                      </span>
                      <span style={{
                        background: '#e9ecef', color: '#495057', padding: '2px 8px',
                        borderRadius: radius.lg, fontSize: '11px', fontWeight: 'bold'
                      }}>
                        {items.length} talep
                      </span>
                      {yeniCount > 0 && (
                        <span style={{
                          background: '#e3f2fd', color: '#1565c0', padding: '2px 8px',
                          borderRadius: radius.lg, fontSize: '11px', fontWeight: 'bold'
                        }}>
                          {yeniCount} yeni
                        </span>
                      )}
                    </div>
                    {/* Toplu seçim (gün bazlı) */}
                    {isSatinalmaOrYonetim && isOpen && yeniCount > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSelectAll(items); }}
                        style={{
                          padding: '3px 10px', fontSize: '11px', border: '1px solid #adb5bd',
                          borderRadius: radius.sm, background: colors.surface, cursor: 'pointer', color: '#495057'
                        }}
                      >
                        Tumunu Sec
                      </button>
                    )}
                  </div>

                  {/* Talep satirlari */}
                  {isOpen && (
                    <div>
                      {items.map((t, idx) => (
                        <div
                          key={t.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '10px 16px',
                            borderBottom: idx < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                            background: selectedIds.has(t.id) ? '#e3f2fd08' : 'transparent'
                          }}
                        >
                          {/* Checkbox — sadece YENI durumda ve satinalma/yonetim icin */}
                          <div style={{ width: '24px', flexShrink: 0 }}>
                            {isSatinalmaOrYonetim && (t.durum === 'YENI' || t.durum === 'BEKLENIYOR') && (
                              <input
                                type="checkbox"
                                checked={selectedIds.has(t.id)}
                                onChange={() => toggleSelect(t.id)}
                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                              />
                            )}
                          </div>

                          {/* Malzeme + Lokasyon */}
                          <div style={{ flex: '2', minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                              {t.malzeme_ad}
                            </div>
                            <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '1px' }}>
                              {t.lokasyon_ad || '-'} | {t.talep_eden_ad} | {formatTime(t.created_at)}
                              {t.açıklama && <span style={{ marginLeft: '6px', fontStyle: 'italic' }}>— {t.açıklama}</span>}
                            </div>
                          </div>

                          {/* Miktar */}
                          <div style={{ width: '80px', textAlign: 'right', fontWeight: '600', fontSize: '14px', color: styles.secondary }}>
                            {t.miktar} {t.birim}
                          </div>

                          {/* Durum badge */}
                          <div style={{ width: '100px', textAlign: 'center' }}>
                            <StatusBadge status={t.durum} />
                          </div>

                          {/* Goruldu bilgisi */}
                          <div style={{ width: '100px', fontSize: '11px', color: '#888' }}>
                            {t.goren_ad && (
                              <div>{t.goren_ad}<br />{formatTime(t.goruldu_tarihi)}</div>
                            )}
                          </div>

                          {/* Aksiyonlar */}
                          <div style={{ width: '120px', display: 'flex', gap: '4px', justifyContent: 'flex-end', flexShrink: 0 }}>
                            {isSatinalmaOrYonetim && (t.durum === 'YENI' || t.durum === 'BEKLENIYOR') && (
                              <button
                                onClick={() => handleGoruldu(t.id)}
                                style={{
                                  padding: '4px 10px', background: '#e65100', color: 'white',
                                  border: 'none', borderRadius: radius.sm, cursor: 'pointer',
                                  fontSize: '11px', fontWeight: 'bold'
                                }}
                              >
                                Gordum
                              </button>
                            )}
                            {t.durum !== 'TEMIN_EDILDI' && t.durum !== 'IPTAL' && (
                              <button
                                onClick={() => handleIptal(t.id)}
                                style={{
                                  padding: '4px 8px', background: 'transparent', color: '#dc3545',
                                  border: '1px solid #dc3545', borderRadius: radius.sm, cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                İptal
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default IhtiyacListesiPage;
